import { supabase } from '@/lib/supabase'
import type {
  DataRequest,
  DataRequestFilters,
  DataRequestType,
  DataRequestStatus,
  ExportFormat,
  DataProcessing,
  ComplianceAudit,
  RGPDStats,
  DataAlert,
  ClientDataExport,
  RGPDCategory,
  ComplianceStatus,
} from '@/types/rgpd'

// STORY-046 to STORY-048: RGPD Service

// Donnees mock pour les regles RGPD (en production, ces donnees viendraient de la BDD)
const mockRGPDCategories: RGPDCategory[] = [
  {
    id: 'lawfulness',
    name: 'Liceite du traitement',
    description: 'Base legale et conditions de licitude',
    icon: 'Scale',
    complianceRate: 85,
    rules: [
      {
        id: 'rule-1',
        category: 'lawfulness',
        title: 'Base legale documentee',
        description: 'Chaque traitement doit avoir une base legale clairement identifiee et documentee.',
        article: 'Art. 6',
        status: 'compliant',
        actions: ['Verifier les bases legales', 'Mettre a jour le registre'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-2',
        category: 'lawfulness',
        title: 'Consentement explicite',
        description: 'Le consentement doit etre libre, specifique, eclaire et univoque.',
        article: 'Art. 7',
        status: 'compliant',
        actions: ['Revoir les formulaires de consentement'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-3',
        category: 'lawfulness',
        title: 'Conditions pour les mineurs',
        description: 'Verification de l\'age et consentement parental pour les moins de 16 ans.',
        article: 'Art. 8',
        status: 'partial',
        actions: ['Implementer la verification d\'age'],
        lastChecked: null,
        notes: 'Non applicable - pas de traitement de donnees de mineurs',
      },
    ],
  },
  {
    id: 'rights',
    name: 'Droits des personnes',
    description: 'Respect des droits d\'acces, rectification, effacement',
    icon: 'UserCheck',
    complianceRate: 100,
    rules: [
      {
        id: 'rule-4',
        category: 'rights',
        title: 'Droit d\'acces',
        description: 'Les personnes peuvent acceder a leurs donnees personnelles.',
        article: 'Art. 15',
        status: 'compliant',
        actions: ['Page d\'export disponible'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-5',
        category: 'rights',
        title: 'Droit de rectification',
        description: 'Les personnes peuvent demander la correction de donnees inexactes.',
        article: 'Art. 16',
        status: 'compliant',
        actions: ['Formulaire de modification accessible'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-6',
        category: 'rights',
        title: 'Droit a l\'effacement',
        description: 'Droit a l\'oubli sous certaines conditions.',
        article: 'Art. 17',
        status: 'compliant',
        actions: ['Procedure de suppression en place'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-7',
        category: 'rights',
        title: 'Droit a la portabilite',
        description: 'Export des donnees dans un format structure et lisible.',
        article: 'Art. 20',
        status: 'compliant',
        actions: ['Export JSON/PDF disponible'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
    ],
  },
  {
    id: 'security',
    name: 'Securite des donnees',
    description: 'Mesures techniques et organisationnelles',
    icon: 'Shield',
    complianceRate: 90,
    rules: [
      {
        id: 'rule-8',
        category: 'security',
        title: 'Chiffrement des donnees',
        description: 'Les donnees sensibles doivent etre chiffrees au repos et en transit.',
        article: 'Art. 32',
        status: 'compliant',
        actions: ['SSL/TLS active', 'Chiffrement BDD'],
        lastChecked: new Date().toISOString(),
        notes: 'Supabase assure le chiffrement',
      },
      {
        id: 'rule-9',
        category: 'security',
        title: 'Controle d\'acces',
        description: 'Acces limite aux personnes autorisees uniquement.',
        article: 'Art. 32',
        status: 'compliant',
        actions: ['RLS active', 'Authentification requise'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-10',
        category: 'security',
        title: 'Journalisation des acces',
        description: 'Tracer les acces aux donnees personnelles.',
        article: 'Art. 32',
        status: 'partial',
        actions: ['Implementer logs d\'audit'],
        lastChecked: null,
        notes: 'Logs partiellement actifs',
      },
    ],
  },
  {
    id: 'retention',
    name: 'Conservation des donnees',
    description: 'Durees de conservation et suppression',
    icon: 'Clock',
    complianceRate: 75,
    rules: [
      {
        id: 'rule-11',
        category: 'retention',
        title: 'Durees definies',
        description: 'Definir des durees de conservation pour chaque categorie de donnees.',
        article: 'Art. 5(1)(e)',
        status: 'compliant',
        actions: ['Politique de conservation etablie'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-12',
        category: 'retention',
        title: 'Suppression automatique',
        description: 'Supprimer les donnees a l\'expiration de la duree de conservation.',
        article: 'Art. 5(1)(e)',
        status: 'partial',
        actions: ['Configurer la suppression auto'],
        lastChecked: null,
        notes: 'A implementer',
      },
      {
        id: 'rule-13',
        category: 'retention',
        title: 'Archivage legal',
        description: 'Conservation des documents a valeur legale (factures, contrats).',
        article: 'Art. 17(3)',
        status: 'compliant',
        actions: ['Archivage securise en place'],
        lastChecked: new Date().toISOString(),
        notes: 'Conservation 10 ans pour les factures',
      },
    ],
  },
  {
    id: 'documentation',
    name: 'Documentation',
    description: 'Registre des traitements et AIPD',
    icon: 'FileText',
    complianceRate: 80,
    rules: [
      {
        id: 'rule-14',
        category: 'documentation',
        title: 'Registre des traitements',
        description: 'Maintenir un registre a jour de tous les traitements.',
        article: 'Art. 30',
        status: 'compliant',
        actions: ['Registre disponible dans l\'application'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-15',
        category: 'documentation',
        title: 'Mentions legales',
        description: 'Informer les personnes de maniere transparente.',
        article: 'Art. 13-14',
        status: 'compliant',
        actions: ['Page mentions legales publiee'],
        lastChecked: new Date().toISOString(),
        notes: null,
      },
      {
        id: 'rule-16',
        category: 'documentation',
        title: 'Analyse d\'impact (AIPD)',
        description: 'Realiser une AIPD pour les traitements a risque.',
        article: 'Art. 35',
        status: 'partial',
        actions: ['Evaluer la necessite d\'une AIPD'],
        lastChecked: null,
        notes: 'A evaluer selon les traitements',
      },
    ],
  },
]

// Mock data processings
const mockDataProcessings: DataProcessing[] = [
  {
    id: 'proc-1',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    name: 'Gestion des clients',
    purpose: 'Gerer les relations commerciales avec les clients',
    legal_basis: 'contract',
    data_categories: ['identity', 'contact', 'financial', 'commercial'],
    recipients: ['Service commercial', 'Service comptabilite'],
    retention_period: '5 ans apres la fin de la relation',
    security_measures: ['Chiffrement', 'Controle d\'acces', 'Sauvegarde'],
    data_transfers: false,
    transfer_details: null,
    dpo_approval: true,
    status: 'compliant',
  },
  {
    id: 'proc-2',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    name: 'Facturation',
    purpose: 'Emettre et suivre les factures clients',
    legal_basis: 'legal_obligation',
    data_categories: ['identity', 'contact', 'financial'],
    recipients: ['Service comptabilite', 'Expert-comptable'],
    retention_period: '10 ans (obligation legale)',
    security_measures: ['Chiffrement', 'Archivage securise'],
    data_transfers: false,
    transfer_details: null,
    dpo_approval: true,
    status: 'compliant',
  },
  {
    id: 'proc-3',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    name: 'Communication marketing',
    purpose: 'Envoyer des newsletters et offres commerciales',
    legal_basis: 'consent',
    data_categories: ['identity', 'contact', 'behavioral'],
    recipients: ['Service marketing'],
    retention_period: '3 ans apres le dernier contact',
    security_measures: ['Chiffrement', 'Gestion des desabonnements'],
    data_transfers: false,
    transfer_details: null,
    dpo_approval: true,
    status: 'compliant',
  },
]

export const rgpdService = {
  // ============================================
  // STORY-046: Liste des regles RGPD
  // ============================================

  async getRGPDCategories(): Promise<RGPDCategory[]> {
    // En production, ces donnees viendraient de la BDD
    return mockRGPDCategories
  },

  async getDataProcessings(): Promise<DataProcessing[]> {
    // En production, depuis Supabase
    // const { data, error } = await supabase
    //   .from('data_processings')
    //   .select('*')
    //   .order('created_at', { ascending: false })
    // if (error) throw error
    // return data
    return mockDataProcessings
  },

  async createDataProcessing(processing: Omit<DataProcessing, 'id' | 'created_at' | 'updated_at'>): Promise<DataProcessing> {
    // En production:
    // const { data, error } = await supabase
    //   .from('data_processings')
    //   .insert(processing)
    //   .select()
    //   .single()
    // if (error) throw error
    // return data
    const newProcessing: DataProcessing = {
      ...processing,
      id: `proc-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    mockDataProcessings.push(newProcessing)
    return newProcessing
  },

  async updateDataProcessing(id: string, processing: Partial<DataProcessing>): Promise<DataProcessing> {
    const index = mockDataProcessings.findIndex(p => p.id === id)
    if (index === -1) throw new Error('Processing not found')
    mockDataProcessings[index] = {
      ...mockDataProcessings[index],
      ...processing,
      updated_at: new Date().toISOString(),
    }
    return mockDataProcessings[index]
  },

  // ============================================
  // STORY-047: Verification de conformite
  // ============================================

  async runComplianceAudit(): Promise<ComplianceAudit> {
    // Simule un audit automatique
    const issues = await this.checkDataAlerts()
    const audit: ComplianceAudit = {
      id: `audit-${Date.now()}`,
      created_at: new Date().toISOString(),
      type: 'automatic',
      status: issues.length === 0 ? 'compliant' : issues.some(i => i.severity === 'critical') ? 'non_compliant' : 'partial',
      issues_found: issues.length,
      issues_resolved: 0,
      issues_pending: issues.length,
      details: issues.map(alert => ({
        id: alert.id,
        severity: alert.severity,
        category: alert.type,
        title: alert.title,
        description: alert.description,
        recommendation: alert.action_required,
        status: 'open',
        resolved_at: null,
        resolved_by: null,
      })),
      report_url: null,
      auditor_id: null,
    }
    return audit
  },

  async checkDataAlerts(): Promise<DataAlert[]> {
    // En production, verifier les donnees reelles
    // Ici on simule quelques alertes
    const alerts: DataAlert[] = [
      {
        id: 'alert-1',
        created_at: new Date(Date.now() - 86400000 * 7).toISOString(),
        type: 'retention_expired',
        severity: 'medium',
        title: 'Donnees de contacts obsoletes',
        description: 'Certains contacts inactifs depuis plus de 3 ans n\'ont pas ete supprimes.',
        affected_records: 12,
        affected_entity: 'contacts',
        entity_ids: ['contact-1', 'contact-2'],
        action_required: 'Supprimer ou archiver les contacts inactifs.',
        resolved: false,
        resolved_at: null,
      },
      {
        id: 'alert-2',
        created_at: new Date(Date.now() - 86400000 * 3).toISOString(),
        type: 'consent_expired',
        severity: 'high',
        title: 'Consentements marketing expires',
        description: 'Le consentement de certains clients pour les communications marketing a expire.',
        affected_records: 5,
        affected_entity: 'clients',
        entity_ids: ['client-1'],
        action_required: 'Demander le renouvellement du consentement ou cesser les communications.',
        resolved: false,
        resolved_at: null,
      },
    ]
    return alerts
  },

  async getComplianceStats(): Promise<RGPDStats> {
    const categories = await this.getRGPDCategories()
    const alerts = await this.checkDataAlerts()

    const overallRate = categories.reduce((sum, cat) => sum + cat.complianceRate, 0) / categories.length

    return {
      totalProcessings: mockDataProcessings.length,
      compliantProcessings: mockDataProcessings.filter(p => p.status === 'compliant').length,
      pendingRequests: 3,
      completedRequests: 15,
      exportRequests: 8,
      deletionRequests: 7,
      lastAuditDate: new Date(Date.now() - 86400000 * 30).toISOString(),
      overallComplianceRate: Math.round(overallRate),
      dataRetentionAlerts: alerts.filter(a => a.type === 'retention_expired').length,
    }
  },

  async exportComplianceReport(): Promise<string> {
    // Genere un rapport de conformite
    const categories = await this.getRGPDCategories()
    const stats = await this.getComplianceStats()
    const alerts = await this.checkDataAlerts()

    const report = {
      generated_at: new Date().toISOString(),
      summary: {
        overall_compliance: stats.overallComplianceRate,
        total_rules: categories.reduce((sum, cat) => sum + cat.rules.length, 0),
        compliant_rules: categories.reduce((sum, cat) => sum + cat.rules.filter(r => r.status === 'compliant').length, 0),
        pending_alerts: alerts.filter(a => !a.resolved).length,
      },
      categories: categories.map(cat => ({
        name: cat.name,
        compliance_rate: cat.complianceRate,
        rules: cat.rules,
      })),
      processings: mockDataProcessings,
      alerts: alerts,
    }

    // En production, genererait un PDF et le stockerait
    return JSON.stringify(report, null, 2)
  },

  // ============================================
  // STORY-048: Export/Suppression des donnees
  // ============================================

  async getDataRequests(filters?: DataRequestFilters): Promise<DataRequest[]> {
    // En production:
    // let query = supabase
    //   .from('data_requests')
    //   .select('*, client:clients(*), processor:admins(*)')
    //   .order('created_at', { ascending: false })
    // ...filtres

    // Donnees mock
    const mockRequests: DataRequest[] = [
      {
        id: 'req-1',
        created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
        updated_at: new Date(Date.now() - 86400000 * 2).toISOString(),
        client_id: 'client-1',
        client_name: 'Jean Dupont',
        client_email: 'jean.dupont@example.com',
        type: 'export',
        status: 'pending',
        format: 'json',
        reason: 'Demande de copie de mes donnees personnelles',
        processed_at: null,
        processed_by: null,
        export_url: null,
        notes: null,
        expires_at: null,
      },
      {
        id: 'req-2',
        created_at: new Date(Date.now() - 86400000 * 5).toISOString(),
        updated_at: new Date(Date.now() - 86400000 * 3).toISOString(),
        client_id: 'client-2',
        client_name: 'Marie Martin',
        client_email: 'marie.martin@example.com',
        type: 'deletion',
        status: 'in_progress',
        reason: 'Je souhaite exercer mon droit a l\'oubli',
        processed_at: null,
        processed_by: 'admin-1',
        notes: 'Verification des obligations legales en cours',
        expires_at: null,
      },
      {
        id: 'req-3',
        created_at: new Date(Date.now() - 86400000 * 10).toISOString(),
        updated_at: new Date(Date.now() - 86400000 * 8).toISOString(),
        client_id: 'client-3',
        client_name: 'Pierre Durand',
        client_email: 'pierre.durand@example.com',
        type: 'export',
        status: 'completed',
        format: 'pdf',
        reason: null,
        processed_at: new Date(Date.now() - 86400000 * 8).toISOString(),
        processed_by: 'admin-1',
        export_url: '/exports/client-3-data.pdf',
        notes: 'Export genere avec succes',
        expires_at: new Date(Date.now() + 86400000 * 7).toISOString(),
      },
      {
        id: 'req-4',
        created_at: new Date(Date.now() - 86400000 * 15).toISOString(),
        updated_at: new Date(Date.now() - 86400000 * 12).toISOString(),
        client_id: 'client-4',
        client_name: 'Sophie Bernard',
        client_email: 'sophie.bernard@example.com',
        type: 'deletion',
        status: 'rejected',
        reason: 'Suppression de mon compte',
        processed_at: new Date(Date.now() - 86400000 * 12).toISOString(),
        processed_by: 'admin-1',
        notes: 'Rejet: factures en cours de paiement (obligation legale de conservation)',
        expires_at: null,
      },
    ]

    let filtered = [...mockRequests]

    if (filters?.search) {
      const search = filters.search.toLowerCase()
      filtered = filtered.filter(
        r => r.client_name.toLowerCase().includes(search) ||
             r.client_email.toLowerCase().includes(search)
      )
    }

    if (filters?.type) {
      filtered = filtered.filter(r => r.type === filters.type)
    }

    if (filters?.status) {
      filtered = filtered.filter(r => r.status === filters.status)
    }

    return filtered
  },

  async getDataRequest(id: string): Promise<DataRequest | null> {
    const requests = await this.getDataRequests()
    return requests.find(r => r.id === id) || null
  },

  async createDataRequest(request: {
    client_id: string
    client_name: string
    client_email: string
    type: DataRequestType
    format?: ExportFormat
    reason?: string
  }): Promise<DataRequest> {
    const newRequest: DataRequest = {
      id: `req-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...request,
      status: 'pending',
      processed_at: null,
      processed_by: null,
      export_url: null,
      notes: null,
      expires_at: null,
    }
    return newRequest
  },

  async processDataRequest(
    id: string,
    action: 'approve' | 'reject',
    adminId: string,
    notes?: string
  ): Promise<DataRequest> {
    const request = await this.getDataRequest(id)
    if (!request) throw new Error('Request not found')

    if (action === 'reject') {
      return {
        ...request,
        status: 'rejected',
        processed_at: new Date().toISOString(),
        processed_by: adminId,
        notes: notes || null,
        updated_at: new Date().toISOString(),
      }
    }

    // Approuver = passer en in_progress puis traiter
    return {
      ...request,
      status: 'in_progress',
      processed_by: adminId,
      notes: notes || 'Traitement en cours',
      updated_at: new Date().toISOString(),
    }
  },

  async completeDataRequest(
    id: string,
    adminId: string,
    result: { export_url?: string; notes?: string }
  ): Promise<DataRequest> {
    const request = await this.getDataRequest(id)
    if (!request) throw new Error('Request not found')

    return {
      ...request,
      status: 'completed',
      processed_at: new Date().toISOString(),
      processed_by: adminId,
      export_url: result.export_url || null,
      notes: result.notes || 'Demande traitee avec succes',
      expires_at: result.export_url ? new Date(Date.now() + 86400000 * 30).toISOString() : null,
      updated_at: new Date().toISOString(),
    }
  },

  async generateClientDataExport(clientId: string): Promise<ClientDataExport> {
    // En production, recupererait les vraies donnees du client
    // const { data: client } = await supabase.from('clients').select('*').eq('id', clientId).single()
    // const { data: projects } = await supabase.from('projects').select('*').eq('client_id', clientId)
    // etc.

    return {
      personal_info: {
        id: clientId,
        full_name: 'Jean Dupont',
        email: 'jean.dupont@example.com',
        phone: '06 12 34 56 78',
        company: 'Entreprise SARL',
        type_client: 'professionnel',
        created_at: new Date(Date.now() - 86400000 * 365).toISOString(),
      },
      billing_info: {
        address: '123 rue de la Paix, 75001 Paris',
        siret: '12345678901234',
        tva: 'FR12345678901',
      },
      projects: [
        {
          id: 'proj-1',
          name: 'Site web entreprise',
          status: 'completed',
          created_at: new Date(Date.now() - 86400000 * 200).toISOString(),
          completed_at: new Date(Date.now() - 86400000 * 150).toISOString(),
        },
        {
          id: 'proj-2',
          name: 'Application mobile',
          status: 'active',
          created_at: new Date(Date.now() - 86400000 * 30).toISOString(),
          completed_at: null,
        },
      ],
      invoices: [
        {
          id: 'inv-1',
          number: 'FAC-2024-001',
          amount: 5000,
          status: 'payee',
          date: new Date(Date.now() - 86400000 * 180).toISOString(),
        },
        {
          id: 'inv-2',
          number: 'FAC-2024-015',
          amount: 2500,
          status: 'en_attente',
          date: new Date(Date.now() - 86400000 * 15).toISOString(),
        },
      ],
      quotes: [
        {
          id: 'quote-1',
          number: 'DEV-2024-001',
          amount: 5000,
          status: 'accepte',
          date: new Date(Date.now() - 86400000 * 210).toISOString(),
        },
      ],
      communications: [
        {
          type: 'email',
          subject: 'Confirmation de projet',
          date: new Date(Date.now() - 86400000 * 200).toISOString(),
        },
        {
          type: 'email',
          subject: 'Envoi du devis',
          date: new Date(Date.now() - 86400000 * 210).toISOString(),
        },
      ],
      consent_history: [
        {
          type: 'marketing',
          given: true,
          date: new Date(Date.now() - 86400000 * 365).toISOString(),
        },
        {
          type: 'newsletter',
          given: true,
          date: new Date(Date.now() - 86400000 * 365).toISOString(),
        },
      ],
    }
  },

  async deleteClientData(clientId: string): Promise<{ success: boolean; deletedItems: string[] }> {
    // En production, supprimerait les donnees du client
    // Attention: verifier les obligations legales avant suppression

    return {
      success: true,
      deletedItems: [
        'Informations personnelles',
        'Historique des communications',
        'Documents non legaux',
        'Preferences et consentements',
      ],
    }
  },

  async exportToFormat(data: ClientDataExport, format: ExportFormat): Promise<string> {
    if (format === 'json') {
      return JSON.stringify(data, null, 2)
    }

    if (format === 'csv') {
      // Simplification - en production utiliser une lib CSV
      const lines = [
        'Type,Donnee',
        `Nom,${data.personal_info.full_name}`,
        `Email,${data.personal_info.email}`,
        `Telephone,${data.personal_info.phone || ''}`,
        `Entreprise,${data.personal_info.company || ''}`,
        `Type client,${data.personal_info.type_client}`,
        `Date inscription,${data.personal_info.created_at}`,
      ]
      return lines.join('\n')
    }

    // Pour PDF, en production utiliser une lib comme jsPDF
    return JSON.stringify(data, null, 2)
  },
}
